import React from "react";
import { WifiOff, RefreshCw, CheckCircle2, AlertTriangle, Database } from "lucide-react";
import { useOffline } from "@/context/OfflineContext";

function formatRelativeTime(isoString) {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

export default function OfflineBanner() {
  const { isOnline, pendingCount, syncStatus, triggerSync, lastSynced, isCacheWarming } = useOffline();

  if (!isOnline) {
    const syncedLabel = formatRelativeTime(lastSynced);
    return (
      <div
        data-testid="offline-banner"
        className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
        style={{ background: "#1E293B", color: "#F8FAFC", borderBottom: "1px solid #334155" }}
      >
        <div className="flex items-center gap-2">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>
            You're offline — changes are saved locally
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: "#334155", color: "#94A3B8" }}>
                {pendingCount} pending
              </span>
            )}
          </span>
        </div>
        {syncedLabel && (
          <span
            data-testid="last-synced-label"
            className="text-xs shrink-0"
            style={{ color: "#94A3B8" }}
          >
            Last synced {syncedLabel}
          </span>
        )}
      </div>
    );
  }

  if (isCacheWarming) {
    return (
      <div
        data-testid="cache-warming-banner"
        className="flex items-center gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
        style={{ background: "#F0F9FF", color: "#0369A1", borderBottom: "1px solid #BAE6FD" }}
      >
        <Database className="w-4 h-4 shrink-0 animate-pulse" />
        <span>Refreshing offline cache in the background…</span>
      </div>
    );
  }

  if (syncStatus === "syncing") {
    return (
      <div
        data-testid="syncing-banner"
        className="flex items-center gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
        style={{ background: "#EFF6FF", color: "#1D4ED8", borderBottom: "1px solid #BFDBFE" }}
      >
        <RefreshCw className="w-4 h-4 shrink-0 animate-spin" />
        <span>Syncing {pendingCount} pending change{pendingCount !== 1 ? "s" : ""}…</span>
      </div>
    );
  }

  if (syncStatus === "synced") {
    return (
      <div
        data-testid="synced-banner"
        className="flex items-center gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
        style={{ background: "#F0FDF4", color: "#166534", borderBottom: "1px solid #BBF7D0" }}
      >
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span>All changes synced</span>
      </div>
    );
  }

  if (syncStatus === "error") {
    return (
      <div
        data-testid="sync-error-banner"
        className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
        style={{ background: "#FEF2F2", color: "#991B1B", borderBottom: "1px solid #FECACA" }}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            Some changes failed to sync —{" "}
            <button
              onClick={triggerSync}
              className="underline underline-offset-2 font-semibold hover:opacity-75"
            >
              Retry
            </button>
          </span>
        </div>
        <span className="text-xs opacity-70">{pendingCount} remaining</span>
      </div>
    );
  }

  return null;
}
